/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../shared-components/forms/NumberedHeading";

export default function CCArray({ control, register, setValue, getValues, errors, inputDir }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.cc',
  });

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{marginTop: '22px'}} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="grow m-0 font-semibold">
                      Caption {index + 1}
                    </Typography>
                    {index !== 0 &&
                      <IconButton 
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                      </IconButton>
                    }
                    {index !== fields.length - 1 &&
                      <IconButton 
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                      </IconButton>
                    }
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <NumberedHeading number="1" title="Start Time (Minute)" instructions="Enter a start time (minute) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.cc.${index}.start_minute`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Start Time (Minute)"
                            placeholder="Start Time (Minute)"
                            error={!!errors.content?.cc?.[index]?.start_minute}
                            helperText={errors.content?.cc?.[index]?.start_minute?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      /> 

                      <NumberedHeading number="2" title="Start Time (Second)" instructions="Enter a start time (second) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.cc.${index}.start_second`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="Start Time (Second)"
                            placeholder="Start Time (Second)"
                            error={!!errors.content?.cc?.[index]?.start_second}
                            helperText={errors.content?.cc?.[index]?.start_second?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="End Time (Minute)" instructions="Enter an end time (minute) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.cc.${index}.end_minute`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="End Time (Minute)"
                            placeholder="End Time (Minute)"
                            error={!!errors.content?.cc?.[index]?.end_minute}
                            helperText={errors.content?.cc?.[index]?.end_minute?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      /> 

                      <NumberedHeading number="4" title="End Time (Second)" instructions="Enter a end time (second) for the focus point" />
                      <Controller
                        control={control}
                        name={`content.cc.${index}.end_second`}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            className="mt-16 mb-16"
                            label="End Time (Second)"
                            placeholder="End Time (Second)"
                            error={!!errors.content?.cc?.[index]?.end_second}
                            helperText={errors.content?.cc?.[index]?.end_second?.message}
                            fullWidth
                            required
                            type="number"
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="5" title="TL Caption" instructions="Enter the caption in the Target Language" />
                      <Controller
                        control={control}
                        name={`content.cc.${index}.tl_text`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="TL Caption"
                            placeholder="TL Caption"
                            id="tl_caption"
                            error={!!errors.content?.cc?.[index]?.tl_text}
                            helperText={errors.content?.cc?.[index]?.tl_text?.message}
                            variant="outlined"  
                            fullWidth
                            required
                            dir={inputDir}
                          />
                        )}
                      />
                          
                      {/* <Divider className="mt-16" />

                      <NumberedHeading number="6" title="EN Caption" instructions="Enter the caption in the English." />
                      <Controller
                        control={control}
                        name={`content.cc.${index}.en_text`}
                        render={({ field }) => (
                          <TextField
                            className="mt-16"
                            {...field}
                            label="EN Caption"
                            placeholder="EN Caption"
                            id="en_caption"
                            error={!!errors.content?.cc?.[index]?.en_text}
                            helperText={errors.content?.cc?.[index]?.en_text?.message}
                            variant="outlined"  
                            fullWidth
                            required
                          />
                        )}
                      /> */}
                      <Divider className="mt-16" />
                      
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>}
                        size="small"
                      >
                        Move Up
                      </Button>
                    }
                    {index !== fields.length - 1 &&
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={<BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>}
                        size="small"
                      >
                        Move Down
                      </Button>
                    }
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={<BASESvgIcon size={20}>heroicons-outline:trash</BASESvgIcon>}
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() => append({ 
            id: BASEUtils.generateGUID(),
            review: {
              development: {
                status: "draft",
                approved: false,
                approval_date: "",
              },
              production: {
                status: "not started",
                approved: false,
                approval_date: "",
              },
            },
            start_minute: 0,
            start_second: 0,
            end_minute: 0,
            end_second: 0,
            tl_text: "",
            en_text: "",
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add caption
          </span>
        </Button>
      </Box>
    </>
  );
}
